import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import UserPool from "./UserPool";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import qs from "qs";
import axios from "axios";
import { WhiteLabelContext } from "../whitelabel";
import { PLANS } from "../plans";
import { COGNITO_STORAGE_OBJECT } from "../../config";

const AccountContext = createContext();

const Account = (props) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userAttributes, setUserAttributes] = useState(null);
  const [idToken, setIdToken] = useState();
  const [accessToken, setAccessToken] = useState();
  const [isLoggedInUsingAdmin, setIsLoggedInUsingAdmin] = useState(false);

  const [sessionLoaded, setSessionLoaded] = useState(false);

  const {
    whitelabelSettings,
    isWhiteLabelOwner,
    setIsWhiteLabelOwner,
    setWhitelabelPlans,
  } = useContext(WhiteLabelContext) ?? {};

  const getSession = useCallback(async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession((err, session) => {
          if (err) {
            setCurrentUser(null);
            reject();
          } else {
            if (session?.idToken?.jwtToken && session?.accessToken?.jwtToken) {
              setIdToken(session?.idToken?.jwtToken);
              setAccessToken(session?.accessToken?.jwtToken);
              setCurrentUser(user);
              resolve(session);

              // Get email from token
              let email = session?.idToken?.payload?.email;

              setUserAttributes({
                sub: session?.idToken?.payload?.sub,
                email: email,
                "custom:family_name":
                  session?.idToken?.payload?.["custom:family_name"],
                "custom:given_name":
                  session?.idToken?.payload?.["custom:given_name"],
                "custom:whitelabel_u":
                  session?.idToken?.payload?.["custom:whitelabel_u"],
              });

              if (
                session?.idToken?.payload?.sub === whitelabelSettings?.user_id
              ) {
                setIsWhiteLabelOwner(true);
                setWhitelabelPlans(PLANS);
              } else {
                setIsWhiteLabelOwner(false);
              }
            } else {
              setIdToken(null);
              setAccessToken(null);
              setCurrentUser(null);
              reject();
            }
          }
        });
      } else {
        setIdToken(null);
        setAccessToken(null);
        setCurrentUser(null);
        reject();
      }
    }).catch((err) => {
      setIdToken(null);
      setAccessToken(null);
      setCurrentUser(null);
      throw err;
    });
  }, [whitelabelSettings?.user_id]);

 

  useEffect(() => {
    setSessionLoaded(false);

    getSession()
      .then((session) => {
        setSessionLoaded(true);
      })
      .catch((err) => {
        setSessionLoaded(true);
      });
  }, []);

  const refreshTokens = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.getSession((err, session) => {
          if (err) {
            // Should we logout user here?
            reject();
          } else {
            if (session?.idToken?.jwtToken && session?.accessToken?.jwtToken) {
              setIdToken(session.idToken.jwtToken);
              setAccessToken(session.accessToken.jwtToken);
              setCurrentUser(user);

              let email = session?.idToken?.payload?.email;
              setUserAttributes({
                sub: session?.idToken?.payload?.sub,
                email: email,
                "custom:family_name":
                  session?.idToken?.payload?.["custom:family_name"],
                "custom:given_name":
                  session?.idToken?.payload?.["custom:given_name"],
                "custom:whitelabel_u":
                  session?.idToken?.payload?.["custom:whitelabel_u"],
              });
              resolve(session);
            } else {
              setIdToken(null);
              setAccessToken(null);
              setCurrentUser(null);
              reject();
            }
          }
        });
      } else {
        // Should we logout user here?
        reject();
      }
    }).catch((err) => {
      // Should we logout user here?
    });
  };

  /** After MFA change: refresh tokens and update React state so API calls succeed. */
  const refreshSessionAndUpdateState = useCallback(() => {
    return new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (!user) {
        reject(new Error("User is not authenticated"));
        return;
      }
      user.getSession((err, session) => {
        if (err || !session?.getRefreshToken?.()) {
          if (err) reject(err);
          else reject(new Error("No refresh token"));
          return;
        }
        const refreshToken = session.getRefreshToken();
        user.refreshSession(refreshToken, (refreshErr, newSession) => {
          if (refreshErr) {
            reject(refreshErr);
            return;
          }
          if (
            newSession?.idToken?.jwtToken &&
            newSession?.accessToken?.jwtToken
          ) {
            setIdToken(newSession.idToken.jwtToken);
            setAccessToken(newSession.accessToken.jwtToken);
            setCurrentUser(user);
            const email = newSession?.idToken?.payload?.email;
            setUserAttributes({
              sub: newSession?.idToken?.payload?.sub,
              email: email,
              "custom:family_name":
                newSession?.idToken?.payload?.["custom:family_name"],
              "custom:given_name":
                newSession?.idToken?.payload?.["custom:given_name"],
              "custom:whitelabel_u":
                newSession?.idToken?.payload?.["custom:whitelabel_u"],
            });
            if (
              newSession?.idToken?.payload?.sub === whitelabelSettings?.user_id
            ) {
              setIsWhiteLabelOwner(true);
              setWhitelabelPlans(PLANS);
            } else {
              setIsWhiteLabelOwner(false);
            }
            resolve(newSession);
          } else {
            reject(new Error("Invalid session after refresh"));
          }
        });
      });
    });
  }, [whitelabelSettings?.user_id]);

  const authenticateWithTokens = async (
    Username,
    AccessToken,
    IdToken,
    RefreshToken,
    TokenType,
    ExpiresIn,
  ) => {
    return await new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: Username,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      user.signInUserSession = user.getCognitoUserSession({
        AccessToken,
        IdToken,
        RefreshToken,
        TokenType,
        ExpiresIn,
      });

      user.cacheTokens();

      getSession()
        .then((session) => {
          resolve();
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  };

  const signUp = async (Email, Firstname, Lastname, Password, WhitelabelU) => {
    return await new Promise((resolve, reject) => {
      const emailAttribute = new CognitoUserAttribute({
        Name: "email",
        Value: Email,
      });
      const firstName = new CognitoUserAttribute({
        Name: "custom:given_name",
        Value: Firstname,
      });
      const lastName = new CognitoUserAttribute({
        Name: "custom:family_name",
        Value: Lastname,
      });
      const whitelabelU = WhitelabelU
        ? new CognitoUserAttribute({
            Name: "custom:whitelabel_u",
            Value: WhitelabelU,
          })
        : null;

      var Username =
        Email.split("@")[0].toLowerCase() +
        "-" +
        Math.floor(Math.random() * 1000000);

      UserPool.signUp(
        Username,
        Password,
        [emailAttribute, firstName, lastName, whitelabelU].filter(Boolean),
        null,
        (err, data) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(data);
        },
      );
    });
  };

  const authenticate = async (Email, Password, TotpCode) => {
    return await new Promise((resolve, reject) => {
      const user = new CognitoUser({
        Username: Email,
        Pool: UserPool,
        ...COGNITO_STORAGE_OBJECT,
      });

      const authDetails = new AuthenticationDetails({
        Username: Email,
        Password: Password,
      });

      user.authenticateUser(authDetails, {
        onSuccess: (data) => {
          window.POPER_UPDATE_USER_ACQUISITION = true;
          getSession()
            .then((session) => {
              resolve(data);
            })
            .catch((err) => {
              reject(err);
            });
        },
        totpRequired: (err) => {
          if (TotpCode) {
            user.sendMFACode(
              TotpCode,
              {
                onSuccess: (data) => {
                  window.POPER_UPDATE_USER_ACQUISITION = true;
                  getSession()
                    .then((session) => {
                      resolve(data);
                    })
                    .catch((err) => {
                      reject(err);
                    });
                },

                onFailure: (err) => {
                  getSession();
                  reject(err);
                },
              },
              "SOFTWARE_TOKEN_MFA",
            );

            return;
          }

          // Do not call getSession() here — we're mid-login, no session yet; would cause "Uncaught (in promise) undefined"
          reject({
            code: "TOTPException",
          });
        },
        onFailure: (err) => {
          getSession();
          reject(err);
        },
      });
      return;
    });
  };

  const verifyUserAccount = async (Email, code) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(
          process.env.REACT_APP_GRIGORA_API_URL + "/general/user/get_username",
          qs.stringify({ email: Email }),
        )
        .then((res) => {
          var Username = res?.data?.Output?.username;

          if (!Username) {
            reject();
            return;
          }

          const user = new CognitoUser({
            Username: Username,
            Pool: UserPool,
            ...COGNITO_STORAGE_OBJECT,
          });

          user.confirmRegistration(code, true, function (err, result) {
            if (err) {
              reject(err);
              return;
            }
            resolve(result);
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const sendUserVerificationCode = async (Email) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(
          process.env.REACT_APP_GRIGORA_API_URL + "/general/user/get_username",
          qs.stringify({ email: Email }),
        )
        .then((res) => {
          var Username = res?.data?.Output?.username;

          if (!Username) {
            reject();
            return;
          }

          const user = new CognitoUser({
            Username: Username,
            Pool: UserPool,
            ...COGNITO_STORAGE_OBJECT,
          });

          user.resendConfirmationCode(function (err, result) {
            if (err) {
              reject(err);
              return;
            }
            resolve(result);
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const changeUserPassword = async (Email, code, password) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(
          process.env.REACT_APP_GRIGORA_API_URL + "/general/user/get_username",
          qs.stringify({ email: Email }),
        )
        .then((res) => {
          var Username = res?.data?.Output?.username;

          if (!Username) {
            reject();
            return;
          }

          const user = new CognitoUser({
            Username: Username,
            Pool: UserPool,
            ...COGNITO_STORAGE_OBJECT,
          });

          user.confirmPassword(code, password, {
            onSuccess() {
              resolve();
            },
            onFailure(err) {
              reject(err);
            },
          });
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const sendForgotPasswordCode = async (Email) => {
    return await new Promise((resolve, reject) => {
      axios
        .post(
          process.env.REACT_APP_GRIGORA_API_URL +
            "/general/user/send_forgot_password_otp",
          qs.stringify({
            email: Email,
            whitelabel_u: "",
          }),
        )
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const logOut = async () => {
    return await new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.signOut(() => {
          // Clear React state only; do not call getSession() — it would trigger
          // extra Cognito calls and can disturb MFA preference / session state.
          setCurrentUser(null);
          setIdToken(null);
          setAccessToken(null);
          setUserAttributes(null);
          setSessionLoaded(true);
          resolve();
        });
      } else {
        setCurrentUser(null);
        setIdToken(null);
        setAccessToken(null);
        setUserAttributes(null);
        setSessionLoaded(true);
        resolve();
      }
    });
  };

  const signInFederated = (identityProvider, callback) => {
    let authHost = "https://grigora.auth.ap-south-1.amazoncognito.com";

    let redirectUri;

    if (
      !process.env.REACT_APP_NODE_ENV ||
      process.env.REACT_APP_NODE_ENV === "development"
    ) {
      redirectUri = `${
        window.location.protocol + "//" + window.location.host
      }/oauth`;
    } else {
      redirectUri = process.env.REACT_APP_GRIGORA_DEPLOY_URL + "/oauth/";
    }
    let responseType = "code";
    let clientId = process.env.REACT_APP_AWS_COGNITO_CLIENT;
    let state = "some_state";
    let scope = "profile email openid aws.cognito.signin.user.admin";
    let access_type = "offline_access";
    let code_challenge = "adsasasdibausydbuasbduhyb";
    let authUrl = `${authHost}/oauth2/authorize?identity_provider=${identityProvider}&code=${code_challenge}&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&state=${state}&scope=${scope}&access_type=${access_type}`;

    var childWindow = window.open(
      authUrl,
      "awsGoogleAuth",
      "location,toolbar,resizable,scrollbars,status,width=600,height=600",
    );

    var maxTries = 0;

    window.addEventListener(
      "message",
      (res) => {
        if (res.data.action === "request_again") {
          // Only request maximum of 5 times.
          if (maxTries > 4) {
            childWindow.close();
            return;
          }
          childWindow.location.replace(authUrl);
          maxTries++;

          return;
        }
        if (res.data.action === "login") {
          let tokensData = res.data.data;

          axios
            .post(
              process.env.REACT_APP_GRIGORA_API_URL +
                "/general/user/oauth_username",
              qs.stringify({ access_token: tokensData.AccessToken }),
            )
            .then((res) => {
              var user = new CognitoUser({
                Username: res?.data?.Output?.data?.Username,
                Pool: UserPool,
                ...COGNITO_STORAGE_OBJECT,
              });

              user.signInUserSession = user.getCognitoUserSession(tokensData);
              user.cacheTokens();
              getSession();

              if (callback) {
                callback();
              }

              return;
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
      false,
    );
  };

  const signInFederatedGoogle = () => {
    let authHost = "https://accounts.google.com";

    let redirectUri;
    if (
      !process.env.REACT_APP_NODE_ENV ||
      process.env.REACT_APP_NODE_ENV === "development"
    ) {
      // let authHost = "https://grigora.auth.ap-south-1.amazoncognito.com";
      redirectUri = `https://grigora.auth.ap-south-1.amazoncognito.com/oauth2/idpresponse`;
    } else {
      redirectUri = process.env.REACT_APP_GRIGORA_DEPLOY_URL + "/oauth/";
    }
    let responseType = "code";
    let clientId =
      "796804839287-vvnm4no80o12bdm12af1n2sbg4drfimu.apps.googleusercontent.com";
    let scope = "profile email openid";
    let authUrl = `${authHost}/o/oauth2/v2/auth?redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${clientId}&scope=${scope}&prompt=consent&access_type=offline`;

    console.log(authUrl);

    var childWindow = window.open(
      authUrl,
      "awsGoogleAuth",
      "location,toolbar,resizable,scrollbars,status,width=600,height=600",
    );

    var maxTries = 0;

    window.addEventListener(
      "message",
      (res) => {
        if (res.data.action === "request_again") {
          // Only request maximum of 5 times.
          if (maxTries > 4) {
            childWindow.close();
            return;
          }
          childWindow.location.replace(authUrl);
          maxTries++;

          return;
        }
        if (res.data.action === "login") {
          let tokensData = res.data.data;

          axios
            .post(
              process.env.REACT_APP_GRIGORA_API_URL +
                "/general/user/oauth_username",
              qs.stringify({ access_token: tokensData.AccessToken }),
            )
            .then((res) => {
              var user = new CognitoUser({
                Username: res?.data?.Output?.data?.Username,
                Pool: UserPool,
                ...COGNITO_STORAGE_OBJECT,
              });

              user.signInUserSession = user.getCognitoUserSession(tokensData);
              user.cacheTokens();
              getSession();
              return;
            })
            .catch((err) => {
              console.log(err);
            });
        }
      },
      false,
    );
  };

  /** Load session on the same user instance used for MFA so SDK sends AccessToken, not Session: null. */
  const ensureUserSession = (user) =>
    new Promise((resolve, reject) => {
      if (!user) {
        reject(new Error("User is not authenticated"));
        return;
      }
      user.getSession((err, session) => {
        if (err) reject(err);
        else if (session?.idToken?.jwtToken && session?.accessToken?.jwtToken)
          resolve(session);
        else reject(new Error("Invalid session"));
      });
    });

  /** Use live user from pool for MFA so we always have the instance with current session (no stale React state). */
  const getLiveCognitoUser = () => {
    const user = UserPool.getCurrentUser();
    if (user) setCurrentUser(user);
    return user;
  };

  const generateCodeForMFA = async () => {
    const user = getLiveCognitoUser();
    if (!user) {
      return Promise.reject(new Error("User is not authenticated"));
    }
    try {
      await ensureUserSession(user);
    } catch (e) {
      return Promise.reject(e);
    }
    return await new Promise((resolve, reject) => {
      user.associateSoftwareToken({
        associateSecretCode: (secretCode) => {
          resolve(secretCode);
        },
        onFailure: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  };

  const verifyAndEnableMFA = async (totpCode, deviceName) => {
    const user = getLiveCognitoUser();
    if (!user) {
      return Promise.reject(new Error("User is not authenticated"));
    }
    try {
      await ensureUserSession(user);
    } catch (e) {
      return Promise.reject(e);
    }
    const friendlyName = deviceName || "My TOTP device";
    return await new Promise((resolve, reject) => {
      user.verifySoftwareToken(totpCode, friendlyName, {
        onSuccess: function (session) {
          var totpMfaSettings = {
            PreferredMfa: true,
            Enabled: true,
          };

          user.setUserMfaPreference(
            null,
            totpMfaSettings,
            function (err, result) {
              if (err) {
                reject(err);
                return;
              }
              // Refresh session so tokens reflect MFA enabled; avoids stale TOTP state on next login
              refreshSessionAndUpdateState()
                .then(() => resolve(result))
                .catch((refreshErr) => {
                  console.warn("Session refresh after MFA enable:", refreshErr);
                  resolve(result);
                });
            },
          );
        },
        onFailure: function (error) {
          console.log(error);
          reject(error);
        },
      });
    });
  };

  const disableMFA = async () => {
    const user = getLiveCognitoUser();
    if (!user) {
      return Promise.reject(new Error("User is not authenticated"));
    }
    try {
      await ensureUserSession(user);
    } catch (e) {
      return Promise.reject(e);
    }
    return await new Promise((resolve, reject) => {
      const totpMfaSettings = {
        PreferredMfa: false,
        Enabled: false,
      };
      user.setUserMfaPreference(
        null,
        totpMfaSettings,
        function (err, result) {
          if (err) {
            console.log(err);
            reject(err);
            return;
          }
          // Cognito SetUserMFAPreference returns {} on success — that's expected.
          // Clear cached user data (incl. MFA settings) so next login does not see stale TOTP requirement
          if (typeof user.clearCachedUserData === "function") {
            user.clearCachedUserData();
          }
          // Second call: refresh session so we get new id/access tokens that reflect MFA off.
          // Without this, the app would still use old tokens and could ask for 2FA again.
          refreshSessionAndUpdateState()
            .then(() => resolve(result))
            .catch((refreshErr) => {
              console.warn("Session refresh after MFA disable:", refreshErr);
              resolve(result);
            });
        },
      );
    });
  };

  useEffect(() => {
    // Refresh Tokens every 120 seconds
    const timer = setInterval(() => {
      refreshTokens();
    }, 2 * 30 * 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <AccountContext.Provider
      value={{
        authenticate,
        signUp,
        getSession,
        logOut,
        currentUser,
        userAttributes,
        idToken,
        accessToken,
        signInFederated,
        signInFederatedGoogle,
        sessionLoaded,
        setSessionLoaded,
        verifyUserAccount,
        sendUserVerificationCode,
        changeUserPassword,
        sendForgotPasswordCode,
        authenticateWithTokens,
        isLoggedInUsingAdmin,
        setIsLoggedInUsingAdmin,
        generateCodeForMFA,
        verifyAndEnableMFA,
        disableMFA,
      }}
    >
      {props.children}
    </AccountContext.Provider>
  );
};
export { Account, AccountContext };
